import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import * as moment from 'moment';
import { IRoomRequest, SearchCriteria } from '../model/hotel.model';
import { AppContext } from './app-context.service';
import Constants from '../pages/checkout/utils/Constants';

@Injectable({
  providedIn: 'root'
})
export class HotelService {
  /** Search Id for search initiated */
  searchId = '';
  currency = 'USD';
  siteContext: any;
  public accountId: any;
  public channelId: any;
  isNewSearchBar: boolean = false;
  /** Search tracing key */
  private _searchKey: any;
  selectedPlace: any;
  // ipAddress = '';
  get searchTracingKey(): string {
    return this._searchKey;
  };
  set searchTracingKey(val) {
    this._searchKey = val;
    this.appContext.searchKey = this._searchKey;
  }

  /** Is Hotel Searched */
  isSeachTypeHotel: any;

  /** Search criteria that user searched for */
  _searchCriteria!: SearchCriteria;
  get searchCriteria(): SearchCriteria {
    if (!this._searchCriteria) {
      this._searchCriteria = new SearchCriteria()
    }
    return this._searchCriteria;
  }
  set searchCriteria(val: any) {
    this._searchCriteria = new SearchCriteria(val);
  }

  /** hotel selected from search results */
  hotelSelectedID: any;

  /** Hotel Price call response */
  hotelPriceID: any;


  /** Default config for room picker */
  // roomConfig = [
  //   {
  //     label: 'Room 1',
  //     passangerTypes: [
  //       {
  //         type: 'Adults',
  //         subtitle: 'Above 12 years',
  //         count: 2,
  //         ageArray: [],
  //         minValue: 1
  //       },
  //       {
  //         type: 'Children',
  //         subtitle: 'Below 12 years',
  //         count: 0,
  //         ageArray: [],
  //         minValue: 0
  //       }
  //     ]
  //   }
  // ];

  /** Selected search option */
  selectedSearchOption = {};

  /** Point multiplier */
  _pointMultiplier: BehaviorSubject<any> = new BehaviorSubject(0.35);

  /** Point multiplier value */
  multiplier?: number;

  /** Redeem points flow flag */
  enableRedeemPointFlow?: boolean;

  /** Earn points flow flag */
  enableEarnPointFlow?: boolean;

  // navigator: any = navigator;

  set pointMultiplier(val: any) {
    this._pointMultiplier.next(val);
    this.multiplier = val;
  }

  constructor(private http: HttpClient, public appContext: AppContext) {
    appContext.siteContext$.subscribe(res => {
      if (res) {
        this.multiplier = res.pointMultiplier;
        this.enableEarnPointFlow = res.enableEarnPointFlow;
        this.enableRedeemPointFlow = res.enableRedeemPointFlow;
      }
    });

  }

  /** Update hotel app state in local storage
   *
   * @param key - key within the appstate
   * @param value - value for that property
   */
  updateStateInLocalStorage(configs: any) {
    const hotelState = JSON.parse(sessionStorage.getItem('hotelState') || '{}') || {};
    Object.keys(configs).forEach(key => {
      hotelState[key] = configs[key];
    });
    sessionStorage.setItem('hotelState', JSON.stringify(hotelState));
    this.selectedPlace = hotelState['selectedSearchOption'];
    sessionStorage.setItem('distancefrom', JSON.stringify(this.selectedPlace))
  }

  getDefaultSearchModel(): any {
    const searchCriteria = {
      checkIn: "",
      checkOut: "",
      rooms: [
        {
          adults: 2,
          children: 0,
          childAges: []
        }
      ]
    };
    return searchCriteria;
  }

  /** Initiate search */
  initSearch(criteria: any, ipAddress: any) {
    let headers = new HttpHeaders();
    headers = headers.set("system-ip", '127.0.0.1');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    if (!criteria) {
      criteria = this.searchCriteria;
    }
    let url = `api/hotels/search/init`;
    return this.http.post(this.addUidOnQueryParams(url), criteria.toServiceModel(), {
      headers: headers
    })
      .pipe(
        tap((res: any) => {
          if (res.status === 'success') {
            criteria.isSearchByHotel = false;
            this.updateStateInLocalStorage({
              searchCriteria: criteria,
              'searchId': res.searchId,
              'searchTracingKey': res.searchTracingKey,
              isSeachTypeHotel: false
            });

            this.searchId = res.searchId;
            this.searchTracingKey = res.searchTracingKey;
            localStorage.setItem(this.searchId + "_searchCriteria", JSON.stringify(criteria.toServiceModel()));
          }
        })
      );
  }

  logPaymentDetails(logPaymentDetails: any, ipAddress: any): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set("system-ip", ipAddress);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(`api/trips/payment/log`, logPaymentDetails, {
      headers: headers
    });
  }

  /**Search Param */
  getSearchResultQueryParam() {
    const criteria = this.searchCriteria;
    const param: any = {
      checkin: moment(criteria.checkIn, 'L').format('YYYYMMDD'),
      checkout: moment(criteria.checkOut, 'L').format('YYYYMMDD'),
      locationid: criteria.locationId,
      name: criteria.fullName
    };
    let roominfo: any[] = [];
    criteria.rooms.forEach(room => {
      let str = room.adults + '';
      if (room.children) {
        str += '-' + room.children;
        str += ':' + room.childAges?.join(':');
      }
      roominfo.push(str);
    });

    param.roompax = roominfo.join('_');
    if (criteria.isSearchByHotel) {
      param.searchbyhotel = true;
    }
    return param;
  }


  getSearchModelFromQueryParam(queryParam: any, searchModel?: SearchCriteria) {
    const request: SearchCriteria = searchModel ? new SearchCriteria(searchModel) : new SearchCriteria();
    if (queryParam.checkin && queryParam.checkout) {
      request.validateAndSetDates(moment(queryParam.checkin, 'YYYYMMDD').format('L'), moment(queryParam.checkout, 'YYYYMMDD').format('L'))
    }

    queryParam.locationid ? request.locationId = queryParam.locationid : request?.locationId;
    queryParam.name ? request.fullName = queryParam.name : request?.fullName;

    if (queryParam.searchbyhotel === 'true') {
      request.isSearchByHotel = true;
    }

    if (queryParam.roompax) {
      request.rooms = [];
      queryParam.roompax.split('_')
        .forEach((str: string) => {
          const paxType: any[] = str.split('-');
          const roomReq: IRoomRequest = {
            adults: +paxType[0]
          }
          if (paxType.length === 2) {
            const childArr: any[] = paxType[1].split(':');
            roomReq.children = +childArr[0];
            const ages = childArr.slice(1);
            roomReq.childAges = [];
            ages.forEach(age => roomReq.childAges?.push(+age));
          } else {
            roomReq.children = 0;
            roomReq.childAges = [];
          }

          request.rooms.push(roomReq);
        });
      request.validateAndSetRooms();
    }
    return request;
  }

  /** Get search results
   *
   * @param limit : number - Number of results to fetch
   * @param offset : number - Offset for results to fetch
   */
  getSearchResults(limit: number = 10, offset: number = -1) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    let url = `api/hotels/search/result/${this.searchId}?limit=${limit}&offset=${offset}`;
    return this.http.get(this.addUidOnQueryParams(url, true), {
      headers: headers
    });
  }

  /** Get filter data */
  getFilterData() {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    let url = `api/hotels/search/result/${this.searchId}/filterdata`;
    return this.http.get(this.addUidOnQueryParams(url), {
      headers: headers
    }
    );
  }

  /** Get autosuggest options */
  getAutosuggestOptions(searchStr: any) {
    // let str = searchStr.replace(/&/g, "%26")
    let str = encodeURIComponent(searchStr)
    return this.http.get(`api/content/autosuggest?term=${str}`, {
      headers: {
        "accountId": this.appContext.siteAccountId
      }
    });
  }

  /** Get sorted results */
  getSortedResults(
    filterCriteria: any,
    sortCriteria: any,
    limit: number = 12,
    offset: number = -1
  ) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(this.addUidOnQueryParams(`api/hotels/search/result/${this.searchId
      }?sortBy=${sortCriteria}&limit=${limit}&offset=${offset}`, true),
      filterCriteria ? filterCriteria : {}, {
      headers: headers
    }
    );
  }

  getHotelNameAutosuggest(searchStr: any) {
    return this.http.get(
      `api/hotels/search/result/${this.searchId}/suggest/${searchStr}`, {
      headers: {
        "search-tracing-key": this.searchTracingKey ? this.searchTracingKey : ''
      }
    }
    );
  }

  getHotelContent() {
    //this.hotelSelectedID = '39769793'//'15651010';
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.get(this.addUidOnQueryParams(`api/hotels/${this.hotelSelectedID}/content`), {
      headers: headers
    });
  }

  /**
   * Fetch guest reviews for a hotel (alldotgay / EAN). Proxies to portal-api
   * POST api/Hotels/guest-reviews which calls Nexus hotelcontent/guestReviews.
   */
  getGuestReviews(requestBody: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    return this.http.post(`api/Hotels/guest-reviews`, requestBody, {
      headers: headers
    });
  }

  getHotelContentMeta(accountId: any, channelId: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    headers = headers.set("accountId", accountId);
    headers = headers.set("channelId", channelId);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.get(this.addUidOnQueryParams(`api/hotels/${this.hotelSelectedID}/content`), {
      headers: headers
    });
  }

  getRooms(ipAddress: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    headers = headers.set("system-ip", ipAddress);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    // console.log(headers.get("search-tracing-key"));
    // console.log(headers.get("system-ip"));
    // console.log(headers.get("currencyCode"));

    return this.http.get(
      this.addUidOnQueryParams(`api/hotels/search/result/${this.searchId}/${this.hotelSelectedID}/rooms`), {
      headers: headers
    }
    );
  }

  getRoomsStateless(body: any, ipAddress: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    headers = headers.set("system-ip", ipAddress);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(
      this.addUidOnQueryParams(`api/hotels/search/${this.hotelSelectedID}/rooms`), body, {
      headers: headers
    }
    );
  }

  getHotelPrice(priceProvider: string, roomRrecommendationId: string, ipAddress: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    headers = headers.set("system-ip", ipAddress);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    let url = `api/hotels/search/${this.searchId}/${this.hotelSelectedID}/price/${priceProvider}/${roomRrecommendationId}`;
    return this.http.get(this.addUidOnQueryParams(url), {
      headers: headers
    }
    );
  }

  getHotelPriceMeta(priceProvider: string, roomRrecommendationId: string, accountId: any, channelId: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    headers = headers.set("accountId", accountId);
    headers = headers.set("channelId", channelId);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.get(
      this.addUidOnQueryParams(`api/hotels/search/${this.searchId}/${this.hotelSelectedID
      }/price/${priceProvider}/${roomRrecommendationId}`), {
      headers: headers
    }
    );
  }


  getPriceProduct(priceId: string) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.get(this.addUidOnQueryParams(`api/trips/${priceId}`), {
      headers: headers
    });
  }

  getPriceProductMeta(priceId: string, accountId: any, channelId: any) {
    let headers = new HttpHeaders();
    headers = headers.set("accountId", accountId);
    headers = headers.set("channelId", channelId);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.get(this.addUidOnQueryParams(`api/trips/${priceId}`),
      {
        headers: headers
      });
  }

  getRulesText(rule: any) {
    var text = "";
    var start = this._getDateStr(rule.start);
    var end = this._getDateStr(rule.end);
    if (rule.value == 0) {
      text = `No cancellation fee if cancellation done till ${end}.`;
    } else if (rule.value === 100 && rule.valueType === 'Percentage') {
      text = `Non refundable if cancellation done after ${start}`;
    } else if (rule.value > 0) {
      var vtype = rule.valueType === "Percentage" ? "%" : rule.valueType;
      var valueFormatted = parseFloat(rule.value).toFixed(2);
      text = `For cancellation done after ${start} and till ${end}, you will incur ${valueFormatted} ${vtype}`;
    }
    return text;
  }

  _getDateStr(str: any) {
    const option = { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true };
    return moment.parseZone(str).format(Constants.dateFormat);
  }

  addToCart(productType: any, priceId: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(`api/cart`, {
      productType: productType,
      priceId: priceId
    }, {
      headers: headers
    });
  }

  getCart() {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.get('api/cart', {
      headers: headers
    });
  }

  getAutoSuggestPopularDestionations() {
    return this.http.get(`api/content/autosuggest/popularlocations`);
  }

  getSearchResultsContentOnly(limit: number = 10, offset: number = -1) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.get(
      this.addUidOnQueryParams(`api/hotels/search/result/${this.searchId
      }/content?filterdata=true&limit=${limit}&offset=${offset}`, true), {
      headers: headers
    }
    );
  }

  getRoomsWithSearchCriteria(body: any, ipAddress: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    headers = headers.set("system-ip", ipAddress);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(this.addUidOnQueryParams(`api/hotels/search/${this.hotelSelectedID}/rooms`), body, {
      headers: headers
    }).pipe(
      tap((res: any) => {
        if (res.status === 'success') {

          this.updateStateInLocalStorage({
            searchCriteria: this.searchCriteria,
            'searchId': res.searchId,
            'searchTracingKey': res.searchTracingKey,
            isSeachTypeHotel: false
          });

          this.searchId = res.searchId;
          this.searchTracingKey = res.searchTracingKey;
          localStorage.setItem(this.searchId + "_searchCriteria", JSON.stringify(this.searchCriteria.toServiceModel()));
        }
      })
    );
  }

  getLatestHotelMapping() {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.get(`api/hotels/${this.hotelSelectedID}/getLatestHotelMapping
    `, {
      headers: headers
    });
  }

  getRoomsWithSearchCriteriaMeta(body: any, accountId: any, channelId: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    headers = headers.set("accountId", accountId);
    headers = headers.set("channelId", channelId);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(`api/hotels/search/${this.hotelSelectedID}/rooms
    `, body, {
      headers: headers
    });
  }

  bookCart(bookModel: any, priceId: any, ipAddress: any): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set("system-ip", '127.0.0.1');
    const hotelState: any = sessionStorage.getItem('hotelState');
    if (hotelState && !this.searchTracingKey) {
      this.searchTracingKey = JSON.parse(hotelState).searchTracingKey;
    }
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(`api/trips/${priceId}/book`, bookModel, {
      headers: headers
    });

  }

  bookCartMeta(bookModel: any, priceId: any, ipAddress: any, accountId: any, channelId: any): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set("system-ip", ipAddress);
    headers = headers.set("accountId", accountId);
    headers = headers.set("channelId", channelId);
    const hotelState: any = sessionStorage.getItem('hotelState');
    if (hotelState && !this.searchTracingKey) {
      this.searchTracingKey = JSON.parse(hotelState).searchTracingKey;
    }
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(`api/trips/${priceId}/book`, bookModel, {
      headers: headers
    });
  }

  pciBookCartMeta(bookModel: any, priceId: any, ipAddress: any, accountId: any, channelId: any): Observable<any> {
    let headers = new HttpHeaders();
    // Guard against undefined being coerced to the literal string 'undefined' in the header value.
    headers = headers.set("system-ip", ipAddress ? ipAddress : '');
    headers = headers.set("accountId", accountId ? accountId : '');
    headers = headers.set("channelId", channelId ? channelId : '');
    const hotelState: any = sessionStorage.getItem('hotelState');
    if (hotelState && !this.searchTracingKey) {
      this.searchTracingKey = JSON.parse(hotelState).searchTracingKey;
    }
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(`api/trips/pci/${priceId}/book`, bookModel, {
      headers: headers
    });
  }

  /**
   * Single source of truth for the post-room-selection checkout route.
   * Precedence: Stripe flow (per existing brand/provider conditions) → PCI checkout (alldotgay
   * only) → guest-details (default for every other brand). Call this from every "select room →
   * go to checkout" site so the decision can never drift between entry points.
   */
  getCheckoutRoute(priceId: string, providerName?: string): string {
    const base = `/checkout/${priceId}`;
    if (this.isStripeCheckoutFlow(providerName)) {
      return `${base}/stripe`;
    }
    if ((this.appContext.siteName || '').toLowerCase() === 'alldotgay') {
      return `${base}/pci-checkout`;
    }
    return `${base}/guest-details`;
  }

  private isStripeCheckoutFlow(providerName?: string): boolean {
    const siteName = this.appContext.siteName || '';
    if (siteName === 'OneStopParking') return true;
    if (siteName.toLowerCase() === 'sharecare') return true;
    if (providerName === 'BookingCom' && siteName === 'Joy') return true;
    return false;
  }

  getBookingDetails(bookingId: any) {
    return this.http.get(`api/trips/bookingdetails/${bookingId}`,
      {
        headers: {
          'search-tracing-key': this.searchTracingKey
            ? this.searchTracingKey
            : '',
        }
      }
    );
  }

  copyTracingKeyToClipboard() {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(this.searchTracingKey);
    }
  }


  getstaticPageContent(pageId: any) {
    return this.http.get(`api/content/staticpage/${pageId}`);
  }

  collectPayment(bookModel: any, priceId: string, ipAddress: string, paymentProvider: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set("system-ip", ipAddress);

    if (this.appContext.isCurrencyChanged.getValue()) {
      headers = headers.set("currencyCode", this.appContext.selectedCurrencyCode.getValue());
    }

    return this.http.post(`api/trips/collect/${priceId}/${paymentProvider}/payment`, bookModel, {
      headers: headers
    });
  }

  getIframeUrl() {
    return this.http.get(
      `api/trips/getSession/`,
      {
        headers: {
          "accountId": this.appContext.siteAccountId
        }
      });
  }

  getTransactionId(body: any, ipAddress: any) {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    headers = headers.set("system-ip", ipAddress);

    return this.http.post(
      `api/trips/3DS/transaction/pci_proxy`, body, {
      headers: headers
    }
    );
  }


  getPeachCheckout(priceId: string, bookingModel: any): Observable<any> {
    return this.http.post(`api/trips/collect/${priceId}/peachpayment`, bookingModel);
  }

  getBookingDetailsHotel(bookingId: any) {
    return this.http.get(`api/trips/bookingdetails/hotel/${bookingId}`,
      {
        headers: {
          'accountId': this.accountId,
          'search-tracing-key': this.searchTracingKey
            ? this.searchTracingKey
            : '',
        },
      }
    );
  }

  deactivateStripePaymentLink(bookingId: string, sid: string) {
    let res = this.http.post(`api/trips/${bookingId}/deactivatePaymentLink?sid=${sid}`, {
      headers: {
        'accountId': this.accountId,
      }
    });
    return res;
  }

  cancelBooking(bookingId: any, ipAddress: any) {
    return this.http.post(`api/trips/booking/${bookingId}/cancel`, null, {
      headers: {
        'search-tracing-key': this.searchTracingKey
          ? this.searchTracingKey
          : '',
        'system-ip': '127.0.0.1',
      },
    });
  }

  villaBookignRequest(payload: any) {
    let res = this.http.post(`notification/email/sendVillaBookingRequest`, payload, {
      headers: {
        "accountId": this.appContext.siteAccountId || ""
      }
    });
    return res;
  }

  getStripeKey() {
    let headers = new HttpHeaders();
    headers = headers.set("search-tracing-key", this.searchTracingKey ? this.searchTracingKey : '');
    // headers = headers.set("accountId", this.appContext.siteAccountId);
    let currency = this.appContext.currency?.code;
    // console.log('currency', currency);
    if (this.appContext.isCurrencyChanged.getValue()) {
      currency = this.appContext.selectedCurrencyCode.getValue();
    }

    return this.http.get(
      `api/stripe/key?currency=${currency}`, {
      headers: headers
    }
    );
  }

  addUidOnQueryParams(url: string, paramExists: boolean = false): string {
    let uid = sessionStorage.getItem("uid");
    if (uid) {
      if (paramExists) {
        url += `&uid=${uid}`;
      } else {
        url += `?uid=${uid}`;
      }
    }
    return url;
  }
}
